import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  MULTI_FAMILY_TABS,
  VIEW_ONLY_MULTI_FAMILY_TABS,
} from 'src/constants/plans.constants'
import {
  ALL_MULTI_FAMILY_PLANS,
  UPDATE_SHELL_PLAN_ATTRIBUTES,
  UPDATE_UNIT_PLAN_ATTRIBUTES,
} from 'src/lib/api-routes'
import { ROUTES } from 'src/lib/routes'
import { IMultiFamilyGridPlan } from 'src/types/multiFamily.grid.interface'
import { UpdatePlanReq } from 'src/types/plan.interface'

import { instance } from '../../lib/axiosInstance'

export interface MultiFamilyPlansState {
  data: IMultiFamilyGridPlan[] | []
  error: string | null
  isLoading: boolean
}

export interface MULTI_FAMILY_PLAN_LISTING {
  allPlans: MultiFamilyPlansState
  divisionPlans: MultiFamilyPlansState
  draftPlans: MultiFamilyPlansState
  isMultiFamilyDivisionPlansUpToDate: boolean
  isMultiFamilyDraftPlansUpToDate: boolean
  isMultiFamilySubmittedPlansUpToDate: boolean
  multiTabValue: string
}

const initialMultiFamilyPlansState: MultiFamilyPlansState = {
  data: [],
  error: null,
  isLoading: false,
}

const determineInitialTab = (): string => {
  const url = location.pathname

  if (url.includes(MULTI_FAMILY_TABS.DIVISION.toLocaleUpperCase())) {
    return MULTI_FAMILY_TABS.DIVISION
  } else if (url.includes(MULTI_FAMILY_TABS.DRAFTS.toLowerCase())) {
    return MULTI_FAMILY_TABS.DRAFTS
  } else if (url.includes(ROUTES.SHELL_PLANS)) {
    return VIEW_ONLY_MULTI_FAMILY_TABS.SHELL_PLANS
  } else if (url.includes(ROUTES.UNIT_PLANS)) {
    return VIEW_ONLY_MULTI_FAMILY_TABS.UNIT_PLANS
  } else if (url.includes(ROUTES.PLANS)) {
    return MULTI_FAMILY_TABS.ALL_PLANS
  }
  return ''
}

export const multiFamilyPlanListInitialState: MULTI_FAMILY_PLAN_LISTING = {
  allPlans: initialMultiFamilyPlansState,
  divisionPlans: initialMultiFamilyPlansState,
  draftPlans: initialMultiFamilyPlansState,
  isMultiFamilyDivisionPlansUpToDate: false,
  isMultiFamilyDraftPlansUpToDate: false,
  isMultiFamilySubmittedPlansUpToDate: false,
  multiTabValue: determineInitialTab(),
}

export const getMultiFamilyAllPlans = createAsyncThunk(
  'multiFamilyPlanListing/getAllPlans',
  async (action?: string) => {
    const response = await instance.get<IMultiFamilyGridPlan[]>(
      ALL_MULTI_FAMILY_PLANS,
      {
        params: {
          action,
          submitted: true,
          tab: MULTI_FAMILY_TABS.ALL_PLANS,
        },
      }
    )
    return response.data || []
  }
)

export const getMultiFamilyDraftPlans = createAsyncThunk(
  'multiFamilyPlanListing/getDraftPlans',
  async (action?: string) => {
    const response = await instance.get<IMultiFamilyGridPlan[]>(
      ALL_MULTI_FAMILY_PLANS,
      {
        params: {
          action,
          submitted: false,
          tab: MULTI_FAMILY_TABS.DRAFTS,
        },
      }
    )

    return response.data || []
  }
)

export const getMultiFamilyDivisionPlans = createAsyncThunk(
  'multiFamilyPlanListing/getDivisionPlans',
  async () => {
    const response = await instance.get<IMultiFamilyGridPlan[]>(
      ALL_MULTI_FAMILY_PLANS,
      {
        params: {
          submitted: true,
          tab: MULTI_FAMILY_TABS.DIVISION,
        },
      }
    )
    return response.data || []
  }
)

export const updateShellPlanAttributes = createAsyncThunk(
  'plans/updateShellPlan',
  async (updatePlanReq: UpdatePlanReq) => {
    return instance
      .patch<IMultiFamilyGridPlan>(
        UPDATE_SHELL_PLAN_ATTRIBUTES(updatePlanReq?.communityPlanId),
        updatePlanReq
      )
      .then((res) => res?.data)
  }
)

export const updateUnitPlanAttributes = createAsyncThunk(
  'plans/updateUnitPlan',
  async (updatePlanReq: UpdatePlanReq) => {
    return instance
      .patch<IMultiFamilyGridPlan>(
        UPDATE_UNIT_PLAN_ATTRIBUTES(updatePlanReq?.communityPlanId),
        updatePlanReq
      )
      .then((res) => res?.data)
  }
)

const updatePlanHiddenProperty = (
  state: MULTI_FAMILY_PLAN_LISTING,
  action: PayloadAction<IMultiFamilyGridPlan>
) => {
  const planCollections = [state.allPlans, state.draftPlans]

  planCollections.forEach((collection) => {
    const changedMultiPlan = collection.data.find(
      (plan) => plan.id === action.payload.id
    )

    if (changedMultiPlan && changedMultiPlan.hidden !== action.payload.hidden) {
      changedMultiPlan.hidden = action.payload.hidden
    }
  })
}

export const multiFamilyPlanListingSlice = createSlice({
  extraReducers: (builder) => {
    builder
      .addCase(getMultiFamilyAllPlans.pending, (state) => {
        state.allPlans.isLoading = true
        state.allPlans.error = null
      })
      .addCase(
        getMultiFamilyAllPlans.fulfilled,
        (state, action: PayloadAction<IMultiFamilyGridPlan[]>) => {
          state.allPlans.isLoading = false
          state.allPlans.data = action.payload
          state.allPlans.error = null
          state.isMultiFamilySubmittedPlansUpToDate = true
        }
      )
      .addCase(getMultiFamilyAllPlans.rejected, (state, action) => {
        state.allPlans.isLoading = false
        state.allPlans.error =
          action.error.message ?? 'Error fetching all multi-family plans'
      })

      .addCase(getMultiFamilyDraftPlans.pending, (state) => {
        state.draftPlans.isLoading = true
        state.draftPlans.error = null
      })
      .addCase(
        getMultiFamilyDraftPlans.fulfilled,
        (state, action: PayloadAction<IMultiFamilyGridPlan[]>) => {
          state.draftPlans.isLoading = false
          state.draftPlans.data = action.payload
          state.draftPlans.error = null
          state.isMultiFamilyDraftPlansUpToDate = true
        }
      )
      .addCase(getMultiFamilyDraftPlans.rejected, (state, action) => {
        state.draftPlans.isLoading = false
        state.draftPlans.error =
          action.error.message ?? 'Error fetching all multi-family draft plans'
      })

      .addCase(getMultiFamilyDivisionPlans.pending, (state) => {
        state.divisionPlans.isLoading = true
        state.divisionPlans.error = null
      })
      .addCase(
        getMultiFamilyDivisionPlans.fulfilled,
        (state, action: PayloadAction<IMultiFamilyGridPlan[]>) => {
          state.divisionPlans.isLoading = false
          state.divisionPlans.data = action.payload
          state.divisionPlans.error = null
          state.isMultiFamilyDivisionPlansUpToDate = true
        }
      )
      .addCase(getMultiFamilyDivisionPlans.rejected, (state, action) => {
        state.divisionPlans.isLoading = false
        state.divisionPlans.error =
          action.error.message ??
          'Error fetching all multi-family divisional plans'
      })
      .addCase(updateShellPlanAttributes.fulfilled, updatePlanHiddenProperty)
      .addCase(updateUnitPlanAttributes.fulfilled, updatePlanHiddenProperty)
  },
  initialState: multiFamilyPlanListInitialState,
  name: 'multiFamilyPlanListing',
  reducers: {
    patchMultiFamilyPlanList: (
      state,
      action: PayloadAction<IMultiFamilyGridPlan[]>
    ) => {
      state.allPlans.data = action.payload
    },
    setMultiFamilyAllPlans: (state, action) => {
      state.allPlans.data = action.payload
    },
    setMultiFamilyDivisionPlanDataUpToDate: (state, action) => {
      state.isMultiFamilyDivisionPlansUpToDate = action.payload
    },
    setMultiFamilyDraftPlanDataUpToDate: (state, action) => {
      state.isMultiFamilyDraftPlansUpToDate = action.payload
    },
    setMultiFamilyDraftPlans: (state, action) => {
      state.draftPlans.data = action.payload
    },
    setMultiFamilySubmittedPlanDataUpToDate: (state, action) => {
      state.isMultiFamilySubmittedPlansUpToDate = action.payload
    },
    setSelectedTab: (state, action: PayloadAction<string>) => {
      state.multiTabValue = action.payload
    },
  },
})

export const {
  patchMultiFamilyPlanList,
  setMultiFamilyAllPlans,
  setMultiFamilyDivisionPlanDataUpToDate,
  setMultiFamilyDraftPlanDataUpToDate,
  setMultiFamilyDraftPlans,
  setMultiFamilySubmittedPlanDataUpToDate,
  setSelectedTab,
} = multiFamilyPlanListingSlice.actions
